import * as React from 'react';

import { Badge as ShadcnBadge } from '@/hen-ui/shadcn/badge';
import { badgeVariants, type BadgeVariantProps } from './badge.variants';
import { cn } from '@/hen-ui/utils';

/**
 * Props for the Badge component
 *
 * Extends shadcn/ui Badge props with HEN Design System variants.
 * The Badge component wraps the shadcn/ui Badge primitive and adds HEN-specific
 * styling through the badgeVariants CVA function.
 */
export interface BadgeProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof ShadcnBadge>,
      'variant' | 'size'
    >,
    Omit<BadgeVariantProps, 'size-md' | 'size-lg' | 'weight'> {
  /**
   * Size from the tablet breakpoint (768px) up. Switches type size, padding,
   * height, and icon size together. Unset keeps `size`.
   */
  'size-md'?: BadgeVariantProps['size-md'];
  /**
   * Size from the desktop breakpoint (1024px) up. Switches type size, padding,
   * height, and icon size together. Unset keeps the smaller-screen size.
   */
  'size-lg'?: BadgeVariantProps['size-lg'];
  /**
   * Label weight for this badge. Unset follows the theme weight
   * (`--cds-badge-font-weight`).
   */
  weight?: BadgeVariantProps['weight'];
  /**
   * Optional leading icon. Catalog authors drop `hen.icon` into this slot.
   * Ignored when `asChild` is set (Radix Slot accepts a single child).
   * React consumers may still compose an icon into `children` instead.
   */
  iconBefore?: React.ReactNode;
  /**
   * Optional trailing icon. Catalog authors drop `hen.icon` into this slot.
   * Ignored when `asChild` is set (Radix Slot accepts a single child).
   */
  iconAfter?: React.ReactNode;
}

/**
 * Badge - A versatile label component for displaying metadata and categories
 *
 * A thin wrapper around shadcn/ui Badge with HEN-specific variants and design tokens.
 * Badges are used to label, categorize, or organize content with keywords that describe them.
 * All styling uses design tokens from tailwind.config.ts.
 *
 * Badge is a static status label. Use Tag Link for compact clickable tags
 * (navigate or Open modal).
 *
 * ## Component Implementation
 * - Wraps shadcn/ui Badge component (from @/shadcn/badge)
 * - Uses Class Variance Authority (CVA) for variant management
 * - Supports asChild pattern for polymorphic behavior (inherited from shadcn)
 * - Uses React.forwardRef for proper ref forwarding
 *
 * ## Variants
 * - **primary** (default): Bold, dark purple background (bg-primary-900) with white text - use for emphasis
 * - **light**: Light background (bg-neutral-100) with purple text (text-primary-700) - use for secondary labels
 * - **neutral**: Subtle gray background (bg-neutral-50) - use for tags and metadata
 * - **outline**: White background with border (border-neutral-200) - use on colored backgrounds
 * - **surfaceAccent**: Theme v2 semantic accent surface with dark text
 * - **tone**: Optional paired semantic treatment that overrides variant colors
 * - **emphasis**: Subtle (default) or strong step for the selected tone
 *
 * ## Sizes
 * - **sm** (default): Compact size for inline labels and tags
 * - **lg**: Larger size for more prominent badges
 * - **xl**: Largest, a 30px status pill
 *
 * `size-md` and `size-lg` switch the whole size from the tablet or desktop
 * breakpoint up. `weight` sets the label weight for one badge.
 *
 * Corner radius and label weight come from shared theme tokens. Each size has
 * default type size, line height, padding, height, and icon size, and a theme
 * can tune any size with its `--cds-badge-{size}-*` tokens.
 *
 * ## Use Cases
 * - Labels: Categorize content (e.g., "Featured", "New", "Popular")
 * - Tags: Show topics, keywords, or attributes
 * - Status: Display state information (e.g., "Active", "Draft", "Published")
 * - Counts: Show numerical data (e.g., "5 courses", "12 reviews")
 *
 * @example
 * // Basic usage
 * <Badge>Featured</Badge>
 *
 * @example
 * // With specific variant and size
 * <Badge variant="light" size="lg">
 *   Trending
 * </Badge>
 *
 * @example
 * // Small on phones, lg on tablets, xl on desktops, regular weight
 * <Badge size="sm" size-md="lg" size-lg="xl" weight="regular">
 *   Open
 * </Badge>
 *
 * @example
 * // Leading icon via the iconBefore slot (catalog and React)
 * import { Icon } from '@highereducation/hen-ui';
 * <Badge variant="primary" iconBefore={<Icon icon="star" aria-hidden />}>
 *   Featured
 * </Badge>
 *
 * @example
 * // Trailing icon via the iconAfter slot (catalog and React)
 * <Badge variant="neutral" iconAfter={<Icon icon="check" aria-hidden />}>
 *   Verified
 * </Badge>
 */
export const Badge = React.forwardRef<
  React.ElementRef<typeof ShadcnBadge>,
  BadgeProps
>(
  (
    {
      className,
      variant,
      children,
      size,
      'size-md': sizeMd,
      'size-lg': sizeLg,
      weight,
      tone,
      emphasis,
      iconBefore,
      iconAfter,
      asChild,
      ...props
    }: BadgeProps,
    ref,
  ) => {
    const content =
      asChild || (iconBefore == null && iconAfter == null) ? (
        children
      ) : (
        <>
          {iconBefore}
          {children}
          {iconAfter}
        </>
      );

    return (
      <ShadcnBadge
        className={cn(
          badgeVariants({
            variant,
            size,
            'size-md': sizeMd,
            'size-lg': sizeLg,
            weight,
            tone,
            emphasis,
          }),
          className,
        )}
        ref={ref}
        asChild={asChild}
        {...props}
      >
        {content}
      </ShadcnBadge>
    );
  },
);

Badge.displayName = 'Badge';
